import { Grid, GridProps } from '@mui/material';
import React, { CSSProperties } from 'react';

type Props = {
  onLastElementView: () => void;
  loadingContent?: React.ReactNode;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
  skeletonCount?: number;
  childContainerProps?: GridProps;
  containerStyles?: GridProps['sx'];
  onFirstElementVisible?: () => void;
  onLastElementVisible?: () => void;
  height?: CSSProperties['height'];
};

function SparkInfiniteScroll({
  onLastElementView,
  children,
  isFetchingNextPage = false,
  isLoading = false,
  loadingContent = <></>,
  skeletonCount = 10,
  childContainerProps = {},
  containerStyles = {},
  onFirstElementVisible,
  onLastElementVisible,
  height = '100%',
}: Props) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const firstElementRef = React.useRef<HTMLDivElement>(null);
  const lastElementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.target === firstElementRef.current && entry.isIntersecting) {
          onFirstElementVisible && onFirstElementVisible();
        }
        if (entry.target === lastElementRef.current) {
          onLastElementVisible && onLastElementVisible();
          if (entry.isIntersecting) {
            onLastElementView();
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root: containerRef.current || null,
      threshold: 0.5,
    });

    if (firstElementRef.current) observer.observe(firstElementRef.current);
    if (lastElementRef.current) observer.observe(lastElementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    children,
    onFirstElementVisible,
    onLastElementView,
    onLastElementVisible,
  ]);

  const getSkeletons = React.useCallback(
    (skeletonCount: number) => {
      return Array.from({ length: skeletonCount }).map((_, index) => (
        <Grid key={index} item {...childContainerProps}>
          {loadingContent}
        </Grid>
      ));
    },
    [childContainerProps, loadingContent]
  );

  const childrenContent = React.useMemo(() => {
    if (isLoading) {
      return <>{!!loadingContent && getSkeletons(skeletonCount)}</>;
    }
    if (Array.isArray(children)) {
      let childrenToRender: React.ReactNode[] = children;
      if (isFetchingNextPage) {
        childrenToRender = [...children, ...getSkeletons(skeletonCount)];
      }

      return (
        <>
          {childrenToRender?.map((child, index) => (
            <Grid
              key={index}
              item
              ref={
                index === 0
                  ? firstElementRef
                  : index === children.length - 1
                    ? lastElementRef
                    : null
              }
              {...childContainerProps}
            >
              {child}
            </Grid>
          ))}
        </>
      );
    }
    return children;
  }, [
    childContainerProps,
    children,
    getSkeletons,
    isFetchingNextPage,
    isLoading,
    loadingContent,
    skeletonCount,
  ]);

  return (
    <Grid
      container
      ref={containerRef}
      sx={{
        scrollBehavior: 'smooth',
        overflowY: 'scroll',
        height: height,
        ...containerStyles,
      }}
    >
      {childrenContent}
    </Grid>
  );
}

export default SparkInfiniteScroll;
